<template>
  <div :class="totalPages ? 'plp' : 'page--cms'">
    <div
      v-if="totalPages"
      class="container"
    >
      <AppBreadcrumbs />

      <div class="plp__container">
        <div class="plp__description">
          <h1 class="plp__heading heading heading--1">
            <span v-if="totalPages">{{ $t('Search results for') }}: '{{ search }}'</span>
            <span v-else>{{ $t('No search results') }}</span>
          </h1>
        </div>

        <div
          v-show="totalPages"
          class="plp__quick-links"
        >
          <div class="plp__quick-links__heading">
            <span class="heading heading--6">{{ $t('Search results for') }}</span>
            <span class="plp__product-count">{{ totalCount }} artikelen</span>
          </div>
        </div>

        <PaginatorToolbar
          v-show="totalPages"
          class="plp__paginator plp__paginator--top"
          :sort="sort"
          :sort-options="sortOptions"
          v-on="paginatorMethods"
        />

        <div class="plp__sidebar">
          <!-- <FilterCategories class="plp__categories" :url-data="urlData" /> -->
          <FilterList
            class="plp__filter-list"
          />
        </div>

        <div
          id="product-grid"
          class="plp__products products"
          :class="`products--${view}`"
        >
          <ProductTile
            v-for="(product, index) in products"
            :key="index"
            :product="product"
            :lazy="index > 2"
            list="ProductListerPage"
          />
        </div>

        <PaginatorToolbar
          class="plp__paginator plp__paginator--bottom"
          :current-page="page"
          :total-pages="totalPages"
          v-on="paginatorMethods"
        />
      </div>
    </div>

    <div v-else>
      <AppBreadcrumbs class="container" />

      <StrapiSlices
        v-if="strapiCmsData?.Slices?.length"
        :slices="strapiCmsData.Slices"
      />
    </div>
    <UiScrollToTop class="plp__scroll-to-top" />
  </div>
</template>

<script lang="ts" setup>
import type { DataLayerItem } from '~layers/app/composables/useDataLayer'
import layoutQuery from '~layers/app/graphql/layout.gql'

const { public: config } = useRuntimeConfig()
const { data: layoutData } = await useAsyncQuery(layoutQuery, { locale: config.strapiLocale, status: 'PUBLISHED' })

const { setBreadcrumbs } = useBreadcrumbs()
const { search } = useCatalogSearch()
const route = useRoute()
const { t } = useI18n({ useScope: 'global' })

const meta = ref({
  page_type: 'search',
  strapi_id: layoutData.value?.search?.[0]?.documentId,
  filters: [{ key: 'stock', values: ['in-stock'] }],
})

provide('meta', meta)

const {
  products,
  fetchProductData,
  strapiData: strapiPlpData,
  getStrapiData: getStrapiPlpData,
} = useCatalog(meta)

await Promise.all([
  fetchProductData(),
  getStrapiPlpData(),
])

const { strapiData: strapiCmsData, getStrapiData: getStrapiCmsData } = useStrapiCms(layoutData.value.noResults?.[0]?.documentId, undefined, undefined, 'cmsPage')
if (layoutData.value.noResults?.[0]?.documentId) {
  await getStrapiCmsData()
}

const {
  page,
  totalPages,
  sort,
  sortOptions,
  view,
  totalCount,
  paginatorMethods,
} = useCatalogPaginator()

const strapiData = computed(() => {
  if (totalCount.value) {
    return strapiPlpData.value?.plpPage || strapiCmsData.value || null
  }
  return strapiCmsData.value || strapiPlpData.value?.plpPage || null
})

const { pageView, viewItemList } = useDataLayer()

const pageTitle = computed(() =>
  useTitle(strapiData.value?.PageTitle),
)

const metaTitle = computed(
  () => strapiData.value?.MetaData?.MetaTitle || pageTitle.value,
)

const metaDescription = computed(
  () => strapiData.value?.MetaData?.MetaDescription || pageTitle.value + ' | ' + t('nuxtSiteConfig.description'),
)

const metaRobots = computed(() => {
  if (strapiData.value?.MetaData?.MetaRobots) {
    return strapiData.value.MetaData?.MetaRobots?.replace('_', ', ')
  }
  return 'noindex, nofollow'
})

pageView('overview', pageTitle.value)

const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({
  item_list_name: 'plp',
  item_list_id: route.fullPath,
}))

useHead({
  title: metaTitle,
  meta: [
    { hid: 'description', name: 'description', content: metaDescription },
    { hid: 'robots', name: 'robots', content: metaRobots },
  ],
})

setBreadcrumbs({
  label: pageTitle.value,
  parents: [],
})

watch(products, () => {
  viewItemList(products.value, dataLayerOverrides.value)
}, { immediate: true })

useHead({
  title: pageTitle.value,
})
</script>

<style>
@import url("~layers/app/pages/~plp.css");
</style>
